/* eslint-disable object-curly-newline,implicit-arrow-linebreak */
import { Alert as MUIAlert, Tooltip, Typography, useTheme } from '@mui/material'
import { getThemeColor } from 'lib/util'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Icon from 'ui/Element/Icon'
import Text from 'ui/Element/Text'
import Button from 'ui/Element/Button'

/**
 * Mapping between severity and colors
 * @type {{success: string, warning: string, error: string, info: string}}
 */
const colorMapping = {
  info: 'info',
  warning: 'warning',
  error: 'error',
  success: 'success',
}

/**
 * Mapping between severity and icons
 * @type {{success: string, warning: string, error: string, info: string}}
 */
const iconMapping = {
  info: 'info',
  warning: 'alert-triangle',
  error: 'close-circle',
  success: 'check-circle',
}

/**
 * Styles for the component
 * @param {Object} theme      the application theme
 * @param {boolean} multiline whether the string is multiline
 * @returns Object
 */
const styles = (theme, { multiline }) => ({
  root: {
    width: '100%',
    borderRadius: '0.25rem',
    padding: [['0.5rem', '0.75rem']],
    '& .MuiAlert-action': {
      padding: 0,
      paddingLeft: '0.5rem',
      marginRight: 0,
      alignItems: 'center',
    },
  },
  ...!multiline && {
    text: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  ...theme.custom.alert,
})

/**
 * Alert UI Element
 *
 * Element to display a text as a MUI alert.
 *
 * @param {Object} p
 * @param {Object} p.forwardedRef           a reference to the root element
 * @param {string} [p.value]                a string to be used as value, this will override
 *                                          `text`
 * @param {string} [p.title]                a string to be used as the title
 * @param {string} [p.text]                 a string to be used as the text
 * @param {string} [p.variant]              a text style variant to set for the element
 * @param {string} [p.textColor]            a color style to override the default text color
 * @param {string} [p.severity]             a severity level
 * @param {string} [p.iconColor=textColor]  a color style to overwrite the default icon color
 * @param {boolean} [p.multiline=false]     whether the text can be multiline
 * @param {Object} [p.endIcon]              end icon to use
 * @param {string} [p.tooltipText]          tooltip text to use
 * @returns {JSX.Element}                   the new Text element
 */
const Alert = ({ forwardedRef, value, title = null, text = null, variant = 'standard', severity = 'info', textColor, iconColor = textColor, multiline = false, ...props }) => {
  const classes = useStyles(styles, { multiline })()
  const theme = useTheme()

  const { onClick = null } = props?.events || {}

  const icon = <Icon
    name={props.icon || iconMapping[severity]}
    {...iconColor && {
      color: iconColor,
    }}
  />

  const endIcon = props.endIcon
    ? <Icon
      source={props.endIcon}
      {...textColor && {
        color: textColor,
      }}
    />
    : null

  return (
    <Tooltip title={props.tooltipText || value}>
      <MUIAlert
        ref={forwardedRef}
        icon={icon}
        className={classes.root}
        variant={variant}
        severity={severity}
        action={
          onClick
            ? <Button
              value={props.actionLabel}
              events={props.events}
              color={colorMapping[severity]}
              variant={'contained'}
              {...props.actionProps}
            />
            : endIcon
        }
      >
        {title && (
          <Text
            variant={'14/bold'}
            className={classes.text}
            {...textColor && {
              color: getThemeColor(theme, textColor),
            }}
            value={title}
          />
        )}
        <Text
          variant={title ? '14/medium' : '14/bold'}
          className={classes.text}
          {...textColor && {
            color: getThemeColor(theme, textColor),
          }}
          value={text || value}
        />
      </MUIAlert>
    </Tooltip>
  )
}

export default useMemoRef(Alert, props => [props.value, props.title, props.text, props.severity])
